import TableWithLoadingWrapper from '../../generic/dataGrid'
import { backendGetRequest } from '../../../helpers/callBackend'
import { useEffect, useReducer } from 'react'
import { errorMessage } from '../../../helpers/errorMessageFunc'
import Typography from '@mui/material/Typography'
import LoadingButton from '@mui/lab/LoadingButton'
import GenericDeleteModal from '../../buttons/genericDeleteModal'
import GenericDropdown from '../../generic/genericDropdown'

const defaultFileState = {
  loading: false,
  totalRecords: -1,
  files: [],
  loadingButtons: {},
}

const filesReducer = (state, action) => {
  switch (action.type) {
    case 'GET_START':
      // dispatch after getting total number of records before starting async
      // pagination calls
      return { ...state, loading: true, totalRecords: action.payload }
    case 'GET_SUCCESS':
      // dispatch after getting new page of data
      // payload must be an array
      // this is actually awesome because it allows us to check loading state
      // outside of using effects
      const newCount = state.files.length + action.payload.length
      let newButtons = {}
      action.payload.forEach((row) => {
        newButtons[row.id] = false
      })
      const newStates = {
        ...state,
        loading: newCount < state.totalRecords,
        files: [...state.files, ...action.payload],
        loadingButtons: { ...state.loadingButtons, ...newButtons },
      }
      return newStates
    case 'REMOVE_FILE':
      const newFiles = state.files.filter((file) => file.id !== action.payload)
      return {
        ...state,
        files: newFiles,
      }
    case 'DOWNLOAD_START':
      return {
        ...state,
        loadingButtons: { ...state.loadingButtons, [action.payload]: true },
      }
    case 'DOWNLOAD_SUCCESS':
      return {
        ...state,
        loadingButtons: { ...state.loadingButtons, [action.payload]: false },
      }
    case 'RESET':
      return defaultFileState
  }
}

/**
 * SFTP files table for a single source group. Meant to be reusable so it loads
 * files within the component.
 *
 * This is also a good example of how we can use Reducers to track loading state
 * of paginated data, rather than needing effects to listen, we can check loading
 * state directly in the update loop.
 *
 * @param {number} bankAccountId - bank account id used to get connection files
 *
 */
const SftpFileCopies = ({ bankAccountId }) => {
  const [filesState, filesDispatch] = useReducer(filesReducer, defaultFileState)

  const getFilesPage = async (pageNum) => {
    try {
      const pageResp = await backendGetRequest('/api/sftp/file-copy/', {
        params: {
          pageIndex: pageNum,
          bankAccountId: bankAccountId,
        },
      })
      if (pageResp?.data?.data) {
        filesDispatch({ type: 'GET_SUCCESS', payload: pageResp?.data?.data })
      } else {
        throw new Error('No file copy data returned')
      }
    } catch (error) {
      errorMessage(error.message)
      return
    }
  }

  const getFiles = async () => {
    let totalPages = 0
    try {
      const pageInfo = await backendGetRequest('/api/sftp/file-copy/', {
        params: {
          pageIndex: 0,
          bankAccountId: bankAccountId,
        },
      })
      totalPages = pageInfo.data.total_pages
      if (pageInfo.data?.total_records > 0) {
        filesDispatch({ type: 'GET_START', payload: pageInfo.data?.total_records })
        for (let pageNum = 1; pageNum <= totalPages; pageNum++) {
          getFilesPage(pageNum)
        }
      }
    } catch (error) {
      errorMessage(error.message)
      return
    }
  }

  const downloadFile = async (fileCopyId) => {
    filesDispatch({ type: 'DOWNLOAD_START', payload: fileCopyId })
    try {
      const fileResp = await backendGetRequest('/api/sftp/file-copy/', { params: { file_copy_id: fileCopyId } })
      if (!fileResp?.data?.data?.body) {
        throw new Error('No data received to download')
      }
      const blob = new Blob([fileResp?.data?.data?.body], { type: 'text/plain' })

      // create temporary blob url, launch in new tab and delete after 10s
      const blobURL = URL.createObjectURL(blob)
      window.open(blobURL, '_blank')
      setTimeout(() => URL.revokeObjectURL(blobURL), 10000)
    } catch (error) {
      errorMessage(error.message)
    }
    filesDispatch({ type: 'DOWNLOAD_SUCCESS', payload: fileCopyId })
  }

  const onDeleteSuccess = async (fileId) => {
    filesDispatch({ type: 'REMOVE_FILE', payload: fileId })
  }

  const columns = [
    {
      field: 'name',
      headerName: 'Filename',
      flex: 2,
      sortable: true,
      sortComparator: (a, b) => a?.localeCompare(b),
      renderCell: (params) => {
        const { row } = params
        return <Typography variant='primary'>{row?.name}</Typography>
      },
    },
    {
      field: 'beneficiary_name',
      headerName: '',
      flex: 1,
      align: 'right',
      renderCell: (params) => {
        const { row } = params
        return (
          <LoadingButton
            disableElevation
            fullWidth
            size='small'
            type='submit'
            variant='contained'
            color='primary'
            loading={filesState.loadingButtons[row.id]}
            loadingPosition='start'
            onClick={() => downloadFile(row.id)}
            sx={{ fontSize: 12, width: '100%', maxWidth: '150px', textTransform: 'none' }}
          >
            Download
          </LoadingButton>
        )
      },
    },
    {
      field: 'actions',
      headerName: '',
      flex: 0.1,
      type: 'actions',
      align: 'right',
      renderCell: (params) => (
        <GenericDropdown
          dropdownItems={[
            <GenericDeleteModal
              type='File Copy'
              title={`Are you sure you want to delete ${params.row.name}?`}
              URL='/api/sftp/file-copy/'
              deleteParams={{ file_copy_id: params.row.id }}
              description={`By clicking Delete below you will no longer have the file copy ${params.row.name} on your dashboard. If you do not want to proceed or unsure, please click X on the upper right.`}
              handleDeleteSuccess={() => onDeleteSuccess(params.row.id)}
              handleDeleteFailure={() => {
                errorMessage(`Failed to delete the file copy ${params.row.name}`)
              }}
            />,
          ]}
        />
      ),
    },
  ]

  useEffect(() => {
    getFiles()
  }, [])

  return <TableWithLoadingWrapper columns={columns} dataSource={filesState.files} loading={filesState.loading} />
}

export default SftpFileCopies
